import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from 'src/common/utils/api';

const POLLING_INTERVAL_MS = 2000;

export type BulkUploadFormat = 'ebs' | 'four_point_wealth';

interface BulkUploadResult {
  clientsCreated: number;
  familiesCreated: number;
  skipped: string[];
}

interface BackgroundProcessResponse {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  statusMessage: string;
  extraDetails: Record<string, string> | null;
}

const isTerminalStatus = (status: BackgroundProcessResponse['status']) =>
  status === 'SUCCESS' || status === 'FAILED';

export const useBulkClientUpload = (companyId: string) => {
  const queryClient = useQueryClient();
  const [backgroundProcessId, setBackgroundProcessId] = useState<string | null>(null);

  const { mutateAsync, isPending: isUploading } = useMutation<
    string,
    Error,
    { format: BulkUploadFormat; file: File }
  >({
    mutationFn: async ({ format, file }) => {
      const { data: presigned } = await api.post<{
        url: string;
        location: string;
        uploadToken: string;
      }>(`admin/companies/${companyId}/clients/bulk-upload/presigned-url`, {
        format,
        fileName: file.name,
      });

      const s3Response = await fetch(presigned.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'text/csv' },
      });
      if (!s3Response.ok) {
        throw new Error(`Failed to upload file to storage: ${s3Response.statusText}`);
      }

      const { data } = await api.post<{ backgroundProcessId: string }>(
        `admin/companies/${companyId}/clients/bulk-upload`,
        { format, uploadToken: presigned.uploadToken },
      );

      return data.backgroundProcessId;
    },
    onSuccess: id => {
      setBackgroundProcessId(id);
    },
  });

  const { data: processStatus } = useQuery<BackgroundProcessResponse>({
    queryKey: ['admin', 'companies', companyId, 'background-processes', backgroundProcessId],
    queryFn: async () => {
      const { data } = await api.get<BackgroundProcessResponse>(
        `admin/companies/${companyId}/background-processes/${backgroundProcessId}`,
      );
      return data;
    },
    enabled: !!backgroundProcessId,
    refetchInterval: query =>
      query.state.data && isTerminalStatus(query.state.data.status) ? false : POLLING_INTERVAL_MS,
  });

  useEffect(() => {
    if (processStatus?.status === 'SUCCESS') {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'clients'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies', companyId, 'families'] });
    }
  }, [processStatus?.status, companyId, queryClient]);

  const isProcessing =
    !!backgroundProcessId && (!processStatus || !isTerminalStatus(processStatus.status));

  const result: BulkUploadResult | null =
    processStatus?.status === 'SUCCESS' && processStatus.extraDetails
      ? {
          clientsCreated: parseInt(processStatus.extraDetails.clients_created ?? '0', 10),
          familiesCreated: parseInt(processStatus.extraDetails.families_created ?? '0', 10),
          skipped: JSON.parse(processStatus.extraDetails.skipped ?? '[]'),
        }
      : null;

  const errorMessage =
    processStatus?.status === 'FAILED' ? processStatus.statusMessage || 'Bulk upload failed' : null;

  const progress: number | null =
    processStatus?.extraDetails?.progress != null && !isTerminalStatus(processStatus.status)
      ? parseInt(processStatus.extraDetails.progress, 10)
      : null;

  const reset = () => {
    setBackgroundProcessId(null);
    if (backgroundProcessId) {
      queryClient.removeQueries({
        queryKey: ['admin', 'companies', companyId, 'background-processes', backgroundProcessId],
      });
    }
  };

  return {
    uploadClients: mutateAsync,
    isPending: isUploading || isProcessing,
    result,
    errorMessage,
    progress,
    reset,
  };
};
